import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, MenuItem, CircularProgress, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Alert,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { stopWheelNudge } from '../utils/numberInput';

/**
 * Restock / write-off / recount — the ONLY way stock changes outside an order.
 *
 * Product edits deliberately cannot touch stock: a `$set` would silently
 * overwrite whatever a concurrent checkout had just reserved, and leave no
 * record of why the number moved. Everything here goes through an atomic,
 * audited endpoint, and the ledger below is the receipt.
 */

const REASONS = [
  { value: 'RESTOCK', label: 'Restock — new inventory arrived', sign: +1 },
  { value: 'WRITE_OFF', label: 'Write-off — damaged / lost', sign: -1 },
  { value: 'ADMIN_ADJUST', label: 'Correction — recount', sign: 0 },
];

const REASON_LABEL: Record<string, string> = {
  INITIAL: 'Opening stock',
  ORDER_RESERVED: 'Order placed',
  ORDER_RELEASED: 'Order cancelled/expired',
  ADMIN_ADJUST: 'Correction',
  RESTOCK: 'Restock',
  WRITE_OFF: 'Write-off',
};

interface Props {
  open: boolean;
  onClose: () => void;
  item: any | null;
  studioId?: string;
  onDone: (msg: string, severity: 'success' | 'error') => void;
}

export default function AdjustStockDialog({ open, onClose, item, studioId, onDone }: Props) {
  const [variantSku, setVariantSku] = useState('');
  const [reason, setReason] = useState('RESTOCK');
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [ledger, setLedger] = useState<any[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const variants: any[] = item?.variants ?? [];
  const hasVariants = variants.length > 0;

  useEffect(() => {
    if (!open) return;
    setVariantSku(hasVariants ? variants[0]?.sku ?? '' : '');
    setReason('RESTOCK');
    setQty('');
    setNote('');
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLedger = useCallback(async () => {
    if (!item?._id || !open) return;
    setLedgerLoading(true);
    try {
      const res: any = await apiService.getMerchandiseStockLedger(item._id, {
        pageSize: 20,
        ...(variantSku ? { variantSku } : {}),
      });
      setLedger(res?.entries || []);
    } catch {
      setLedger([]);
    } finally {
      setLedgerLoading(false);
    }
  }, [item?._id, open, variantSku]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const currentStock = hasVariants
    ? variants.find(v => v.sku === variantSku)?.stockQuantity ?? 0
    : item?.stockQuantity ?? 0;

  const reasonDef = REASONS.find(r => r.value === reason)!;
  const magnitude = Number(qty || 0);

  // RESTOCK is always +, WRITE_OFF always −, and a correction takes the sign the
  // admin typed. Deriving it from the reason removes the "did I need a minus?"
  // question that makes recounts go wrong.
  const delta =
    reasonDef.sign === 0 ? magnitude : reasonDef.sign * Math.abs(magnitude);

  const resulting = currentStock + delta;
  const invalid =
    !qty || magnitude === 0 || !Number.isInteger(magnitude) || resulting < 0;

  const submit = async () => {
    if (invalid || !item) return;
    setSaving(true);
    try {
      await apiService.adjustMerchandiseStock(
        item._id,
        {
          ...(variantSku ? { variantSku } : {}),
          delta,
          reason,
          ...(note ? { note } : {}),
        },
        studioId,
        // Pinned to the item's own location — under "All locations" the
        // switcher sends __all__, which the server refuses for writes.
        studioIdOf((item as any).studio),
      );
      onDone(`Stock updated — ${item.title} is now ${resulting}`, 'success');
      onClose();
    } catch (err: any) {
      onDone(err?.response?.data?.message || 'Failed to adjust stock', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Adjust stock — {item?.title}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {hasVariants && (
            <TextField select size="small" label="Variant" value={variantSku}
              onChange={e => setVariantSku(e.target.value)}>
              {variants.map(v => (
                <MenuItem key={v.sku} value={v.sku}>
                  {v.label} — {v.sku} ({v.stockQuantity ?? 0} in stock)
                </MenuItem>
              ))}
            </TextField>
          )}

          <ToggleButtonGroup size="small" exclusive value={reason} onChange={(_, v) => v && setReason(v)} fullWidth>
            {REASONS.map(r => (
              <ToggleButton key={r.value} value={r.value} sx={{ textTransform: 'none', fontSize: 12 }}>
                {r.label.split(' — ')[0]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            {reasonDef.label}
          </Typography>

          <TextField
            size="small" type="number" label={reasonDef.sign === 0 ? 'Change (+/−)' : 'Quantity'}
            value={qty} onChange={e => setQty(e.target.value)} onWheel={stopWheelNudge}
            helperText={
              reasonDef.sign === 0
                ? 'Positive adds, negative removes'
                : reasonDef.sign > 0 ? 'Units received' : 'Units removed'
            }
          />

          <TextField size="small" label="Note (optional)" value={note} multiline rows={2}
            placeholder="e.g. damaged in transit"
            onChange={e => setNote(e.target.value)} />

          <Box display="flex" alignItems="center" gap={2} p={1.5} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="body2">Current <strong>{currentStock}</strong></Typography>
            <Typography variant="body2" color={delta >= 0 ? 'success.main' : 'error.main'}>
              {delta >= 0 ? '+' : ''}{delta || 0}
            </Typography>
            <Typography variant="body2">→ <strong>{resulting}</strong></Typography>
          </Box>
          {resulting < 0 && <Alert severity="error">Stock cannot go below zero.</Alert>}

          {/* The audit trail — the whole reason stock is never a blind $set. */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary">RECENT MOVEMENTS</Typography>
            {ledgerLoading ? (
              <Box display="flex" justifyContent="center" py={2}><CircularProgress size={18} /></Box>
            ) : ledger.length === 0 ? (
              <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                No movements recorded
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><Typography variant="caption" fontWeight={700}>When</Typography></TableCell>
                      <TableCell><Typography variant="caption" fontWeight={700}>Reason</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption" fontWeight={700}>Δ</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption" fontWeight={700}>Balance</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledger.map(e => (
                      <TableRow key={e._id}>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(e.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }}
                            label={e.order?.orderNumber ?? REASON_LABEL[e.reason] ?? e.reason} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color={e.delta >= 0 ? 'success.main' : 'error.main'} fontWeight={700}>
                            {e.delta >= 0 ? '+' : ''}{e.delta}
                          </Typography>
                        </TableCell>
                        <TableCell align="right"><Typography variant="caption">{e.balanceAfter}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={invalid || saving}>
          {saving ? <CircularProgress size={18} /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
